"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Loopmark } from "@/components/brand";
import { captureAnalytics } from "@/lib/analytics";
import { useViewer } from "@/lib/use-viewer";

/**
 * Two instances of the same bar, never one that moves.
 *
 * At rest the nav belongs to the hero: it sits in normal flow inside the dark
 * plate, so it never crosses the lime frame. Once you scroll past the hero it
 * is replaced by a `floating` copy fixed to the viewport. Rendering both and
 * cross-fading is what keeps the hero content from jumping the moment the bar
 * would otherwise leave the flow.
 */
function NavBar({ floating }: { floating: boolean }) {
  // Resolved in the browser, never on the server. Reading the auth cookie here
  // would force every page carrying this bar to render per-request, which is
  // the exact cost that was 503-ing the site.
  const { subscribed, comped } = useViewer();
  return (
    <nav
      className={`flex h-[76px] w-full items-center justify-between ${
        floating
          ? ""
          : ""
      }`}
    >
      <Link href="/" className="ls-loop flex items-center gap-2.5">
        <Loopmark size={24} />
        <span className="text-[17px] font-semibold tracking-[-0.02em] text-silver" style={{ fontFamily: '"Space Grotesk", "Space Grotesk", "Manrope", sans-serif' }}>Loop Studio</span>
      </Link>
      <div className="flex items-center gap-5 sm:gap-8">
        <div className="hidden items-center gap-7 text-[14.5px] font-medium lg:flex">
        <Link
          href="/#work"
          onClick={() => captureAnalytics("cta_clicked", { location: "navigation", target: "film" })}
          className="lp-text-link text-white/65 transition-colors hover:text-white"
        >
          Work
        </Link>
        <Link
          href="/#how"
          onClick={() => captureAnalytics("cta_clicked", { location: "navigation", target: "how" })}
          className="lp-text-link text-white/65 transition-colors hover:text-white"
        >
          How it works
        </Link>
        <Link href="/pricing" className="lp-text-link text-white/65 transition-colors hover:text-white">Pricing</Link>
        </div>
        {/* Someone who already pays for this should not be sold it again — send
            them where they were trying to go.

            Everyone else used to be sent STRAIGHT TO STRIPE from here, on a
            button that read "Start · $49/mo". This bar is sticky, so that made
            a price the one permanent call to action on the page — visible
            before a single frame of the product had been demonstrated. The
            replays from the video launch show what that costs: one visitor
            took the price at 15 seconds and immediately went backwards looking
            for what it bought, and nobody in ten recordings typed a character.
            The free first video is the offer now; the price lives in the
            pricing section, which is where someone goes once they want it.

            This also retires the prefetch hazard the old comment described:
            next/link prefetches, /api/checkout/start mints a Stripe Checkout
            Session, and prefetching it created a live abandoned session on
            every page load for every visitor. Nothing here mints anything. */}
        {subscribed || comped ? (
          <Link
            href="/studio"
            onClick={() => captureAnalytics("cta_clicked", { location: "navigation", target: "studio" })}
            className="lp-nav-cta"
          >
            Open Studio <span aria-hidden="true">↗</span>
          </Link>
        ) : (
          // /start decides on the server: the plans when signed out, the
          // studio for a signed-in customer with access, the plan choice for
          // one without. Not prefetched: the answer depends on the session.
          <Link
            href="/start#plans"
            prefetch={false}
            onClick={() => captureAnalytics("cta_clicked", { location: "navigation", target: "free_start" })}
            className="lp-nav-cta"
          >
            Edit my first video <span aria-hidden="true">→</span>
          </Link>
        )}
      </div>
    </nav>
  );
}

/** The copy that lives inside the hero plate. Always present, never moves. */
export function LandingNavInline() {
  return (
    <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <NavBar floating={false} />
    </div>
  );
}

/**
 * The copy that travels with you, once the hero bar has scrolled away.
 *
 * Fading in at 140px is only correct on a page that also renders
 * `LandingNavInline` inside its hero — there, the inline bar covers the top of
 * the page and this one takes over. A page without a hero has no inline copy,
 * so the same behaviour leaves it with NO navigation at all until you scroll,
 * which is how contact, pricing and the legal pages ended up with no way out.
 * Those pass `alwaysVisible`.
 */
export function LandingNav({ alwaysVisible = false }: { alwaysVisible?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (alwaysVisible) return;
    const onScroll = () => setScrolled(window.scrollY > 140);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [alwaysVisible]);

  const shown = alwaysVisible || scrolled;
  return (
    <header
      className={`ls-public-nav inset-x-0 top-0 w-full border-b border-line text-silver transition-all duration-300 ${
        shown ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-3 opacity-0"
      }`}
      aria-hidden={!shown}
    >
      <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <NavBar floating />
      </div>
    </header>
  );
}
